import { createFileRoute } from '@tanstack/react-router';
import { analyticsSearchSchema, buildDateRange } from '@/features/analytics/dateRange';
import type { DateRange } from '@/features/analytics/types';
import { useAnalyticsFunnel, useAnalyticsRetention, useAnalyticsActiveUsers } from '@/features/analytics/api';
import { FunnelChart } from '@/features/analytics/funnel/FunnelChart';
import { StickinessKpi } from '@/features/analytics/funnel/StickinessKpi';
import { ActiveUsersChart } from '@/features/analytics/funnel/ActiveUsersChart';
import { RetentionGrid } from '@/features/analytics/funnel/RetentionGrid';
import { FirstCookTrend } from '@/features/analytics/funnel/FirstCookTrend';

export const Route = createFileRoute('/_authed/analytics/funnel')({
  validateSearch: (raw: Record<string, unknown>) => {
    const parsed = analyticsSearchSchema.safeParse(raw);
    return parsed.success ? parsed.data : {};
  },
  component: FunnelPage,
});

/** 84-day floor for the retention grid — ensures at least 12 weekly cohorts. */
function extendToMinRetentionWindow(range: DateRange): DateRange {
  const toDate = new Date(range.to + 'T00:00:00Z');
  const fromDate = new Date(range.from + 'T00:00:00Z');
  const spanDays = Math.round((toDate.getTime() - fromDate.getTime()) / 86_400_000);
  if (spanDays >= 84) return range;
  // Stretch `from` back to make the window at least 84 days.
  const extended = new Date(toDate);
  extended.setUTCDate(extended.getUTCDate() - 84);
  return { ...range, from: extended.toISOString().slice(0, 10) };
}

function FunnelPage() {
  const rawSearch = Route.useSearch();
  const range = buildDateRange({
    preset: rawSearch.preset,
    from: rawSearch.from,
    to: rawSearch.to,
  });

  // Retention needs at least 12 weekly cohorts (84 days).  Extend `from` when
  // the page-level range is shorter, without changing the controls for other charts.
  const retentionRange = extendToMinRetentionWindow(range);
  const retentionAutoExtended = retentionRange.from !== range.from;

  const funnel = useAnalyticsFunnel(range);
  const retention = useAnalyticsRetention(retentionRange);
  const activeUsers = useAnalyticsActiveUsers(range);

  if (funnel.isLoading || retention.isLoading || activeUsers.isLoading) {
    return <div className="text-ink-soft text-sm py-8">Loading…</div>;
  }

  if (funnel.isError || retention.isError || activeUsers.isError) {
    return (
      <div
        className="rounded-xl p-4 text-sm"
        style={{
          background: 'var(--color-error-bg)',
          border: '1px solid var(--color-error-border)',
          color: 'var(--color-error-text)',
        }}
      >
        Failed to load funnel analytics. Try refreshing or check that rollup data is available.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="analytics-funnel-page">
      <section>
        <h2 className="font-serif text-lg mb-1">Signup funnel</h2>
        <p className="text-xs text-ink-faint mb-3">
          Note: The funnel currently collapses <code>signup_started</code> and{' '}
          <code>signup_completed</code> into a single <code>signup</code> step —
          pending REC-87 rollup follow-up to split.
        </p>
        <FunnelChart steps={funnel.data?.steps ?? []} />
      </section>

      <section>
        <h2 className="font-serif text-lg mb-3">Active users &amp; activity ratio</h2>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <StickinessKpi rows={activeUsers.data?.rows ?? []} />
          <ActiveUsersChart rows={activeUsers.data?.rows ?? []} range={range} />
        </div>
        <p className="text-xs text-ink-faint mt-2">
          Approximation: &ldquo;Active user-days (7d / 28d)&rdquo; are rolling sums of daily active
          users, not counts of distinct users over the window. A user active every day
          contributes 7 to the 7-day figure rather than 1. True distinct-user WAU/MAU
          requires a separate deduplication rollup (REC-94 follow-up).
        </p>
      </section>

      <section>
        <h2 className="font-serif text-lg mb-1">Retention cohorts</h2>
        {retentionAutoExtended && (
          <p className="text-xs text-ink-faint mb-3">
            Range auto-extended to 84 days (12 weekly cohorts) for this section — the
            page date range is shorter than 12 weeks.
          </p>
        )}
        <RetentionGrid rows={retention.data?.rows ?? []} />
      </section>

      <section>
        <h2 className="font-serif text-lg mb-3">First cook trend</h2>
        <FirstCookTrend rows={retention.data?.rows ?? []} />
      </section>
    </div>
  );
}
